// components/DepartmentSwitchModal.jsx
import React, { useState, useEffect } from 'react';
import './DepartmentModalSwitch.css';
import apiService from '../services/api'; 

const DepartmentSwitchModal = ({ isOpen, onClose, currentDepartment, onDepartmentSwitch, availableDepartments }) => {
  const [isSwitching, setIsSwitching] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!isOpen) return;
      
      try {
        setLoading(true);
        const userEmail = sessionStorage.getItem('user_emailId');
        if (!userEmail) {
          console.error('User email not found');
          return;
        }
        
        const response = await apiService.fetchData(`get-single-manager-department?manager_email=${encodeURIComponent(userEmail)}`);
        if (response && response.data) {
          setDepartments(response.data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDepartmentSwitch = (newDept) => {
    if (newDept === currentDepartment) return;
    
    try {
      setIsSwitching(true);

      // Update session storage and cookie immediately
      sessionStorage.setItem('user_department', newDept);
      document.cookie = `user_department=${encodeURIComponent(newDept)}; path=/; max-age=86400`;

      // Show success message
      setShowSuccess(true);

      // Auto-refresh page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Department switch failed:', error);
      alert(error.message || 'Failed to switch department.');
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="dept-switch-overlay" onClick={!isSwitching ? onClose : undefined}>
      <div className="dept-switch-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="dept-switch-header">
          <div className="dept-switch-header-content">
            <div className="dept-switch-icon-wrapper">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div>
              <h3>Department Switch</h3>
              <p>Switch between available departments</p>
            </div>
          </div>
          <button 
            className="dept-switch-close" 
            onClick={onClose}
            disabled={isSwitching}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="dept-switch-body">
          {/* Success Message */}
          {showSuccess && (
            <div className="success-message">
              <div className="success-icon">
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="success-title">Department switched successfully!</p>
                <p className="success-subtitle">Your session has been updated.</p>
              </div>
            </div>
          )}

          {/* Departments Section */}
          <div className="current-dept-section">
            <div className="current-dept-label">Available Departments</div>
            
            {loading ? (
              <div className="loading-message" style={{ padding: '20px' }}>
                <div className="spinner"></div>
                <p>Loading departments...</p>
              </div>
            ) : (
              departments.map((dept, index) => {
                const isCurrentDept = dept.toLowerCase() === currentDepartment.toLowerCase();
                return (
                  <div 
                    key={index}
                    className={`current-dept-card ${isCurrentDept ? 'active-dept' : ''}`}
                    onClick={() => !isSwitching && !isCurrentDept && handleDepartmentSwitch(dept)}
                    style={{ 
                      cursor: isSwitching || isCurrentDept ? 'not-allowed' : 'pointer',
                      opacity: isSwitching ? 0.6 : 1
                    }}
                  >
                    <div className="current-dept-icon">
                      <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong className="current-dept-title">
                        {dept}
                      </strong>
                      <p className="current-dept-subtitle">
                        {isCurrentDept ? 'Current active department' : 'Click to switch to this department'}
                      </p>
                    </div>
                    {isCurrentDept && (
                      <div className="active-badge">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>



          {/* Loading State */}
          {isSwitching && (
            <div className="loading-message">
              <div className="spinner"></div>
              <p>Switching department...</p>
            </div>
          )}

          {/* Info Note */}
          <div className="dept-switch-note">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4m0-4h.01" />
            </svg>
            <div>
              <p className="note-title">Important Note</p>
              <p className="note-text">
                Switching departments will update your permissions immediately. 
                Some features may become available or restricted based on the selected department.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="dept-switch-footer">
          <button 
            className="dept-switch-close-btn" 
            onClick={onClose}
            disabled={isSwitching}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentSwitchModal;